import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import {
  COMPANY_STATUS_MANAGEMENT_URL,
  GET_ADD_COMPANY_SUBSCRIPTION_URL,
  GET_ADD_REPRESENTATIVES_URL,
  GET_CREATE_COMPANIES_URL,
  GET_UPDATE_DELETE_COMPANY_URL,
  GET_UPDATE_DELETE_PLAN_URL,
} from "@/constants/api/endpoints"
import { http } from "@/utils/http"
import { AxiosResponse } from "axios"
import { CompanyFormType, CompanyType1 } from "@/types/CompaniesType"
import { APIPaginatedResponseType, APIPaginationType, ExtraRequestParams } from "@/types/api/utils"
import { PricingFormType } from "@/types/PricingForm"
import { cleanObject } from "@/utils/utils"
import { EnterpriseFormType } from "@/types/EnterpriseForm"
import any = jasmine.any

// Fetch all companies
export const fetchCompanies = createAsyncThunk<APIPaginatedResponseType<CompanyType1> | CompanyType1[], ExtraRequestParams>(
  "companies/fetchCompanies",
  async (extraParams, thunkApi) => {
    const params = new URLSearchParams(extraParams ? cleanObject(extraParams) : {})
    const url = params.toString() ? `${GET_CREATE_COMPANIES_URL}?${params.toString()}` : `${GET_CREATE_COMPANIES_URL}`
    // console.log(url)
    return http
      .get<APIPaginatedResponseType<CompanyType1> | CompanyType1[]>(url)
      .then((res): APIPaginatedResponseType<CompanyType1> | CompanyType1[] => res.data)
      .catch(e => {
        return thunkApi.rejectWithValue({ data: e.data })
      })
  })

// Create a company
export const createAsyncCompany = createAsyncThunk<CompanyType1, EnterpriseFormType | FormData >(
  "companies/createAsyncCompany",
  async (company: Partial<EnterpriseFormType | FormData>, thunkApi) => {
    return http
      .post<CompanyType1>(GET_CREATE_COMPANIES_URL, company)
      .then((res): CompanyType1 => res.data)
      .catch(e => {
        return thunkApi.rejectWithValue({ data: e.data })
      })
  },
)




// Update an existing company
export const updateAsyncCompany = createAsyncThunk<CompanyType1, {
  id?: string,
  data?: Partial<EnterpriseFormType | FormData>,
}>(
  "companies/updateAsyncCompany",
  async ({ id, data }: {
    id?: string,
    data?: Partial<EnterpriseFormType | FormData>,
  }, thunkApi) => {
    return http
      .patch<CompanyType1>(GET_UPDATE_DELETE_COMPANY_URL.replace(":id", `${id}`), data)
      .then((res): CompanyType1 => res.data)
      .catch(e => {
        return thunkApi.rejectWithValue({ data: e.data })
      })
  },
)



// Update an existing company
export const asyncAddSubscriptionToCompany = createAsyncThunk<any, {
  companyId: string,
  data?: Partial<PricingFormType | FormData>,
}>(
  "companies/asyncAddSubscriptionToCompany",
  async ({ companyId, data }: {
    companyId: string,
    data?: Partial<PricingFormType | FormData>,
  }, thunkApi) => {
    return http
      .post(GET_ADD_COMPANY_SUBSCRIPTION_URL.replace(":id", `${companyId}`), data)
      .then(res => res.data)
      .catch(e => {
        return thunkApi.rejectWithValue({ data: e.data })
      })
  },
)

export const asyncAddLegalPerson = createAsyncThunk<any, {
  companyId: string,
  data?: any,
}>(
  "companies/asyncAddLegalPerson",
  async ({ companyId, data }: {
    companyId: string,
    data?: any,
  }, thunkApi) => {
    return http
      .post(GET_ADD_REPRESENTATIVES_URL.replace(":id", `${companyId}`), data)
      .then(res => res.data)
      .catch(e => {
        return thunkApi.rejectWithValue({ data: e.data })
      })
  },
)

// Manage an existing company' s status
/*export const asyncManageCompanyStatus = createAsyncThunk<any, {
  id: string,
  data?: any,
}>(
  "companies/asyncManageCompanyStatus",
  async ({ id, data }: {
    id: string,
    data?: any,
  }, thunkApi) => {
    return http
      .post(GET_ADD_REPRESENTATIVES_URL.replace(":id", `${id}`), data)
      .then(res => res.data)
      .catch(e => {
        return thunkApi.rejectWithValue({ data: e.data })
      })
  },
)
 */

// Delete an existing company
export const deleteAsyncCompany = createAsyncThunk<AxiosResponse, { id: string }>(
  "companies/deleteAsyncCompany",
  async ({ id }: { id: string }, thunkApi) => {
    return http
      .delete<AxiosResponse>(GET_UPDATE_DELETE_PLAN_URL.replace(":id", id))
      .then(res => res.data)
      .catch(e => {
        return thunkApi.rejectWithValue({ data: e.data })
      })
  },
)


// initial state
const initialState: {
  companies: CompanyType1[],
  pagination?: APIPaginationType
  companiesStatus: string,
  companiesErrors?: any,

  addCompanyStatus: string,
  addCompanyErrors?: any,

  deleteCompanyErrors?: any,
  deleteCompanyStatus: string,
} = {
  companies: [],
  companiesStatus: "idle",
  addCompanyStatus: "idle",
  deleteCompanyStatus: "idle",
}

const companiesSlice = createSlice({
  name: "companies",
  initialState,
  reducers: {},

  extraReducers(builder) {
    // get  all companies
    // Todo handle pagination
    builder
      .addCase(fetchCompanies.pending, (state) => {
        state.companiesStatus = "loading"
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.companiesStatus = "succeeded"
        if ("results" in action.payload) {
          const { results, ...pagination } = action.payload
          state.companies = results
          state.pagination = pagination
        } else {
          state.companies = action.payload
        }
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.companiesStatus = "failed"
        state.companiesErrors = action.error
      })

    builder
      .addCase(createAsyncCompany.pending, (state) => {
        state.addCompanyStatus = "loading"
      })
      .addCase(createAsyncCompany.fulfilled, (state, action) => {
        state.addCompanyStatus = "succeeded"
        state.companies = [action.payload, ...state.companies]
        // Todo: automatically fetch companies list

      })
      .addCase(createAsyncCompany.rejected, (state, action) => {
        state.addCompanyStatus = "failed"
        state.addCompanyErrors = action.error
      })

    builder
      .addCase(updateAsyncCompany.pending, (state) => {
        state.companiesStatus = "loading"
      })
      .addCase(updateAsyncCompany.fulfilled, (state, action) => {
        state.companiesStatus = "succeeded"
        const index = state.companies.findIndex(company => company.id === action.meta.arg.id)
        state.companies[index] = action.payload
        // if (index >= 0) {
        //   state.companies.splice(index, 1)
        //   state.companies.reverse()
        //   // Todo Find the updated company and update it or make a global fetch
        //   state.companies.unshift(action.payload)
        //   state.companies.reverse()
        // }

      })
      .addCase(updateAsyncCompany.rejected, (state, action) => {
        state.companiesStatus = "failed"
        state.companiesErrors = action.error
      })

    builder
      .addCase(asyncAddSubscriptionToCompany.pending, (state) => {
        state.companiesStatus = "loading"
      })
      .addCase(asyncAddSubscriptionToCompany.fulfilled, (state, action) => {
        state.companiesStatus = "succeeded"
        // const { data } = action.payload;
        // const companyIndex = state.companies.findIndex((award) => award.id == data.companyId);
        // let updatedCompany = state.companies[companyIndex]
        // updatedCompany.active_subscription = data
        // state.companies[companyIndex] = updatedCompany;
      })
      .addCase(asyncAddSubscriptionToCompany.rejected, (state, action) => {
        state.companiesStatus = "failed"
        state.companiesErrors = action.error
      })

    builder
      .addCase(deleteAsyncCompany.pending, (state) => {
        state.deleteCompanyStatus = "loading"
      })
      .addCase(deleteAsyncCompany.fulfilled, (state, action) => {
        state.companiesStatus = "succeeded"
        state.companies = state.companies.filter((company) => company.id !== action.meta.arg.id)
      })
      .addCase(deleteAsyncCompany.rejected, (state, action) => {
        state.deleteCompanyStatus = "failed"
        state.deleteCompanyErrors = action.error
      })
  },
})

export default companiesSlice.reducer

